/**
 * What a landed Google lookup SAYS, shown as a panel rather than a payload.
 *
 * A demand or trends lookup used to open as a JSON block in the drawer, which
 * is where a person went for "more details" and found a dict. These are the
 * details: the Trends line at a readable size with its numbers and the rising
 * queries, and the Ads rows as a table with bars. The same panels serve the
 * lookup item itself, the item it hangs under (an idea, a product, a type),
 * and the profile page — one rendering of one fact, wherever it is met.
 *
 * `DemandTermsTable` is the Demand report's table, lifted out so the profile
 * of a product or a type can show the terms that are about it without a
 * second table drifting from the first.
 */
import { useMemo } from 'react';
import type { BoardroomDemandTerm } from '../../../types';
import Sparkline, { VolumeBar } from './Sparkline';
import DataTable from './DataTable';
import type { SeenMarks } from '../useSeen';
import type { Column } from './tableColumns';

/** The shape `trends.interest._snapshot_payload` writes into a LOOKUP_TRENDS payload. */
export interface TrendPayload {
  keyword?: string;
  geo?: string;
  timeframe?: string;
  interest_recent?: number | null;
  interest_a_year_ago?: number | null;
  change_pct?: number | null;
  rising?: ({ query?: string; value?: number | string; formatted?: string } | string)[];
  top?: ({ query?: string; value?: number | string; formatted?: string } | string)[];
  series?: { time?: string; t?: string; value?: number | null; v?: number | null }[];
  fetched_at?: string;
  explore_url?: string;
}

/** One Google Ads row as the lookup stores it. */
export interface DemandRow {
  keyword?: string;
  monthly_searches?: number | null;
  growth_pct?: number | null;
  competition?: string | null;
  top_bid?: number | null;
}

export function pct(v: number | null | undefined): string {
  if (v === null || v === undefined) return '';
  const n = Math.round(v * 100);
  return `${n > 0 ? '+' : ''}${n}%`;
}

export function pctClass(v: number | null | undefined): string {
  if (v === null || v === undefined) return 'text-gray-400';
  if (v > 0.05) return 'text-emerald-700 dark:text-emerald-300';
  if (v < -0.05) return 'text-rose-700 dark:text-rose-300';
  return 'text-gray-500';
}

function num(v: unknown): number | null {
  return typeof v === 'number' && Number.isFinite(v) ? v : null;
}

function queryText(q: { query?: string; value?: number | string; formatted?: string } | string): { query: string; value: string } {
  if (typeof q === 'string') return { query: q, value: '' };
  return { query: String(q.query ?? ''), value: String(q.formatted ?? q.value ?? '') };
}

export function seriesPoints(t: TrendPayload): { t: string; v: number | null }[] {
  return (t.series ?? []).map(p => ({ t: String(p.time ?? p.t ?? ''), v: num(p.value ?? p.v) }));
}

/**
 * The Trends line at a size a person can read, with the numbers and the
 * queries that are rising alongside it. `compact` is the inline form for a
 * parent item's drawer: the line, the change, and the first few rising queries.
 */
export function TrendPanel({ trend, compact = false, testId }: { trend: TrendPayload; compact?: boolean; testId?: string }) {
  const points = seriesPoints(trend);
  const change = num(trend.change_pct) !== null ? (num(trend.change_pct) as number) / 100 : null;
  const rising = (trend.rising ?? []).map(queryText).filter(q => q.query);
  const top = (trend.top ?? []).map(queryText).filter(q => q.query);
  const kw = trend.keyword ?? '';
  return (
    <div className="text-xs" data-testid={testId}>
      <div className="flex items-center gap-3 flex-wrap">
        <Sparkline points={points} width={compact ? 120 : 280} height={compact ? 28 : 64} label={`Google Trends interest for ${kw}`} testId={testId ? `${testId}-spark` : undefined} />
        <div className="flex flex-col">
          <span className="text-gray-800 dark:text-gray-100">
            {!compact && <span className="font-medium">{kw} · </span>}
            <span className="font-mono">
              {num(trend.interest_a_year_ago) ?? '?'}→{num(trend.interest_recent) ?? '?'}
            </span>
            {change !== null && <span className={`ml-1 font-mono ${pctClass(change)}`}>{pct(change)}</span>}
          </span>
          <span className="text-[11px] text-gray-400">
            interest a year ago → now{trend.geo ? ` · ${trend.geo}` : ' · worldwide'}{trend.timeframe ? ` · ${trend.timeframe}` : ''}
            {points.length === 0 && ' · no series stored'}
          </span>
        </div>
      </div>
      {rising.length > 0 && (
        <div className="mt-1.5">
          <span className="text-[11px] uppercase tracking-wide text-gray-400">Rising</span>
          <ul className="flex flex-wrap gap-1 mt-0.5">
            {(compact ? rising.slice(0, 4) : rising).map(q => (
              <li key={q.query} className="px-1.5 py-0.5 rounded-full border border-gray-200 dark:border-gray-700 text-xs text-gray-700 dark:text-gray-200">
                {q.query}
                {q.value && <span className="ml-1 text-gray-400">{q.value}</span>}
              </li>
            ))}
          </ul>
        </div>
      )}
      {!compact && top.length > 0 && (
        <div className="mt-1.5">
          <span className="text-[11px] uppercase tracking-wide text-gray-400">Top</span>
          <ul className="flex flex-wrap gap-1 mt-0.5">
            {top.map(q => (
              <li key={q.query} className="px-1.5 py-0.5 rounded-full border border-gray-200 dark:border-gray-700 text-xs text-gray-600 dark:text-gray-300">
                {q.query}
                {q.value && <span className="ml-1 text-gray-400">{q.value}</span>}
              </li>
            ))}
          </ul>
        </div>
      )}
      {!compact && trend.explore_url && (
        <a href={trend.explore_url} target="_blank" rel="noreferrer" className="mt-1.5 inline-block text-xs text-indigo-600 dark:text-indigo-400 hover:underline">
          Open in Google Trends ↗
        </a>
      )}
    </div>
  );
}

/** The Ads rows a demand lookup returned, as a table with bars; related terms below, dimmed. */
export function DemandRowsPanel({ rows, related = [], compact = false, testId, room }: { rows: DemandRow[]; related?: DemandRow[]; compact?: boolean; testId?: string; room?: string }) {
  const all = [...rows.map(r => ({ ...r, seeded: true })), ...related.map(r => ({ ...r, seeded: false }))].filter(r => r.keyword);
  const shown = compact ? all.filter(r => r.seeded).slice(0, 8) : all;
  const max = Math.max(0, ...all.map(r => num(r.monthly_searches) ?? 0));
  const columns = useMemo<Column<DemandRow & { seeded: boolean }>[]>(() => {
    const full: Column<DemandRow & { seeded: boolean }>[] = [
      {
        key: 'term',
        label: 'Term',
        width: compact ? 52 : 34,
        always: true,
        flipLabel: '',
        value: r => String(r.keyword ?? ''),
        cell: r => (
          <>
            {r.keyword}
            {!r.seeded && <span className="ml-1 text-[11px] italic">related</span>}
          </>
        ),
      },
      {
        key: 'volume',
        label: 'Searches/mo',
        width: compact ? 24 : 16,
        align: 'right',
        value: r => num(r.monthly_searches),
        cellClass: () => 'font-mono whitespace-nowrap',
        cell: r => (
          <>
            <VolumeBar value={num(r.monthly_searches)} max={max} width={compact ? 40 : 64} />
            <span className="ml-1.5">
              {num(r.monthly_searches) !== null ? Math.round(num(r.monthly_searches) as number).toLocaleString() : '-'}
            </span>
          </>
        ),
      },
      {
        key: 'growth',
        label: 'Growth',
        width: compact ? 24 : 14,
        align: 'right',
        value: r => num(r.growth_pct),
        cellClass: r => `font-mono ${pctClass(num(r.growth_pct) !== null ? (num(r.growth_pct) as number) / 100 : null)}`,
        cell: r => {
          const g = num(r.growth_pct) !== null ? (num(r.growth_pct) as number) / 100 : null;
          return g !== null ? pct(g) : '-';
        },
      },
      {
        key: 'competition',
        label: 'Competition',
        width: 20,
        value: r => (r.competition ? String(r.competition).toLowerCase() : ''),
        cell: r => (r.competition ? String(r.competition).toLowerCase() : '-'),
      },
      {
        key: 'bid',
        label: 'Top bid',
        width: 16,
        align: 'right',
        value: r => num(r.top_bid),
        cellClass: () => 'font-mono',
        cell: r => (num(r.top_bid) !== null ? `$${(num(r.top_bid) as number).toFixed(2)}` : '-'),
      },
    ];
    // COMPACT DROPS TWO COLUMNS rather than guarding every cell that draws
    // them: a shorter list, not a table with holes in it.
    return compact ? full.slice(0, 3) : full;
  }, [compact, max]);

  if (shown.length === 0) return <span className="text-xs text-gray-400 italic">Google returned no rows.</span>;
  return (
    <div data-testid={testId}>
      <DataTable
        table={compact ? 'demand-rows-compact' : 'demand-rows'}
        file="demand-rows"
        room={room}
        columns={columns}
        rows={shown}
        rowKey={r => (testId ? `${testId}-${String(r.keyword).replace(/\s+/g, '-')}` : `${r.seeded ? 's' : 'r'}-${r.keyword}`)}
        rowClass={r => (r.seeded ? '' : 'text-gray-400')}
      />
      {compact && all.length > shown.length && <div className="text-[11px] text-gray-400 mt-0.5">and {all.length - shown.length} more on the lookup</div>}
    </div>
  );
}

/** Interest now against a year ago, in words, with the direction — and the shape. */
function TrendCell({ t, keyword }: { t: BoardroomDemandTerm['trend']; keyword: string }) {
  if (!t) return <span className="text-gray-300 dark:text-gray-600">-</span>;
  const arrow = t.change === null ? '' : t.change > 0.05 ? '↑' : t.change < -0.05 ? '↓' : '→';
  return (
    <span className="inline-flex items-center gap-2">
      <Sparkline points={t.series} label={`Google Trends interest for ${keyword}`} testId={`spark-${keyword.replace(/\s+/g, '-')}`} />
      <span className={`font-mono ${pctClass(t.change)}`} title={t.rising.length ? `rising: ${t.rising.join(', ')}` : undefined}>
        {arrow} {t.interest_a_year_ago ?? '?'}→{t.interest_recent ?? '?'}
        {t.change !== null && <span className="ml-1">{pct(t.change)}</span>}
      </span>
    </span>
  );
}

/** The Demand report's table: joined Ads + Trends rows, seeded first, related dimmed. */
/** ONE DESCRIPTION PER COLUMN. `value` is what the file gets and what sort
 *  compares — sorting by searches orders by the NUMBER, not by the formatted
 *  string, which is the difference between 9,000 before 10,000 and after. */
export function demandColumns(
  onOpen: (itemId: number) => void,
  maxTerm: number,
  showAbout: boolean,
): Column<BoardroomDemandTerm>[] {
  return [
    {
      key: 'term',
      label: 'Term',
      width: 24,
      always: true,
      value: t => t.keyword,
      cell: t => (
        <>
          <button
            type="button"
            onClick={() => onOpen(t.trend?.lookup_id ?? t.lookup_id)}
            className="hover:underline text-left"
            title={t.seeded ? 'asked for · open the lookup' : 'offered by Google as related · open the lookup'}
          >
            {t.keyword}
          </button>
          {!t.seeded && <span className="ml-1 text-[11px] italic">related</span>}
          {t.evidence > 0 && <span className="ml-1 text-[11px] text-emerald-600 dark:text-emerald-400" title="written up as evidence">✓</span>}
        </>
      ),
    },
    {
      key: 'volume',
      label: 'Searches/mo',
      width: 12,
      align: 'right',
      value: t => t.volume,
      cell: t => (
        <span className="font-mono whitespace-nowrap">
          <VolumeBar value={t.volume} max={maxTerm} testId={`bar-${t.keyword.replace(/\s+/g, '-')}`} />
          <span className="ml-1.5">{t.volume !== null ? Math.round(t.volume).toLocaleString() : '-'}</span>
        </span>
      ),
    },
    {
      key: 'growth',
      label: 'Ads growth',
      width: 12,
      align: 'right',
      value: t => t.growth,
      cell: t => <span className={`font-mono ${pctClass(t.growth)}`}>{t.growth !== null ? pct(t.growth) : '-'}</span>,
    },
    {
      key: 'trend',
      label: 'Trends, year ago → now',
      width: 18,
      // Sorted by the CHANGE, which is the thing a reader is asking about.
      value: t => t.trend?.change ?? null,
      cell: t => <TrendCell t={t.trend} keyword={t.keyword} />,
    },
    { key: 'competition', label: 'Competition', width: 14, value: t => t.competition },
    {
      key: 'bid',
      label: 'Top bid',
      width: 10,
      align: 'right',
      value: t => t.top_bid,
      cell: t => <span className="font-mono">{t.top_bid !== null ? `$${t.top_bid.toFixed(2)}` : '-'}</span>,
    },
    ...(showAbout
      ? [
          {
            key: 'about',
            label: 'About',
            width: 10,
            sortable: false,
            value: (t: BoardroomDemandTerm) => t.about.map(a => a.title).join(' / '),
            cell: (t: BoardroomDemandTerm) => (
              <>
                {t.about.map((a, i) => (
                  <span key={a.id}>
                    {i > 0 && ', '}
                    <button type="button" onClick={() => onOpen(a.id)} className="hover:underline text-left text-gray-700 dark:text-gray-200">
                      {a.title}
                    </button>
                  </span>
                ))}
              </>
            ),
          } as Column<BoardroomDemandTerm>,
        ]
      : []),
  ];
}

/** The Demand report's table: joined Ads + Trends rows, seeded first, related dimmed. */
export function DemandTermsTable({
  terms,
  onOpen,
  showAbout = true,
  table = 'demand',
  room,
  unseen,
  onSeen,
  seen,
}: {
  terms: BoardroomDemandTerm[];
  onOpen: (itemId: number) => void;
  /** The "About" column; off on a profile, where every row is about the page. */
  showAbout?: boolean;
  /** Its own memory. A profile shows a different set of columns from the
   *  report, so it remembers a different choice of them. */
  table?: string;
  room?: string;
  /** Ids that changed and have not been looked at. */
  unseen?: ReadonlySet<number>;
  /** Ids now on screen — the caller drops them from the badge. */
  onSeen?: (ids: number[]) => void;
  /** THE REPORT'S tracker, where the report has other tables too. A term row
   *  is keyed by its KEYWORD, which no item id names, so this table marks
   *  nothing — but a tracker of its own still pruned the report's marks. */
  seen?: SeenMarks;
}) {
  const maxTerm = Math.max(0, ...terms.map(t => t.volume ?? 0));
  const columns = useMemo(
    () => demandColumns(onOpen, maxTerm, showAbout),
    [onOpen, maxTerm, showAbout],
  );
  return (
    <DataTable
      table={table}
      room={room}
      unseen={seen ? undefined : unseen}
      onSeen={seen ? undefined : onSeen}
      seen={seen}
      columns={columns}
      rows={terms}
      rowKey={t => `demand-term-${t.keyword.replace(/\s+/g, '-')}`}
      rowClass={t => (t.seeded ? '' : 'text-gray-400')}
    />
  );
}
